import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { OverlayId } from './types';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { hideOverlay, showOverlay } from 'Client/redux/overlays/actions';
import useRouter from 'Client/hooks/useRouter';
import usePrevious from 'Client/hooks/usePrevious';

export const OVERLAY_QUERY_PARAM = 'overlay';

/**
 * if overlay is history state, we should use it
 * fallback to query-parameter overlay
 */
const getCurrentOverlayId = (search: string, state?: unknown) => {
    if (typeof state === 'object' && state?.[OVERLAY_QUERY_PARAM]) {
        return state?.[OVERLAY_QUERY_PARAM];
    }

    const queryParams = new URLSearchParams(search);
    return queryParams.get(OVERLAY_QUERY_PARAM) || '';
};
const composeUrl = (pathname: string | undefined = '', search?: string) => {
    const sanitizedSearch = new URLSearchParams(search).toString();

    if (sanitizedSearch) {
        return `${pathname}?${sanitizedSearch}`;
    }

    return pathname;
};

export const useOverlayUrlService = () => {
    const bellaPush = useBellaPush();
    const {
        location: { state, pathname, search },
        history,
    } = useRouter();

    const currentOverlayId = getCurrentOverlayId(search, state);

    const hasAnActiveOverlay = !!currentOverlayId;

    const pushOverlay = (overlayId: OverlayId) => {
        const currentUrl = composeUrl(pathname, search);

        if (currentOverlayId !== overlayId) {
            bellaPush(currentUrl, false, { [OVERLAY_QUERY_PARAM]: overlayId });
        }
    };

    const pullOffOverlay = (goToPreviousPageOnClose = true) => {
        if (!hasAnActiveOverlay) {
            return;
        }
        if (goToPreviousPageOnClose) {
            history.goBack();
            return;
        }

        const queryParams = new URLSearchParams(search);
        if (queryParams.has(OVERLAY_QUERY_PARAM)) {
            queryParams.delete(OVERLAY_QUERY_PARAM);
        }
        const newUrl = composeUrl(pathname, queryParams.toString());

        bellaPush(newUrl, true, {
            [OVERLAY_QUERY_PARAM]: '',
        });
    };

    const isActiveOverlay = (overlayId: OverlayId) => overlayId === currentOverlayId;

    return { pushOverlay, pullOffOverlay, hasAnActiveOverlay, isActiveOverlay };
};

const useOverlayStateFromUrl = () => {
    const dispatch = useDispatch();
    const {
        location: { state, search },
    } = useRouter();

    const overlayId = getCurrentOverlayId(search, state);
    const previousOverlayId = usePrevious(overlayId);

    useEffect(() => {
        if (overlayId) {
            dispatch(showOverlay(overlayId));
        } else if (previousOverlayId) {
            dispatch(hideOverlay(previousOverlayId));
        }
    }, [dispatch, overlayId, previousOverlayId]);
};

export default useOverlayStateFromUrl;
